/**
 * Stores API — typed wrappers around `/api/storelocations/*`.
 *
 * Household-scoped store SELECTION: list, update (preferences only), delete
 * (unselect), toggle favorite. TD39 — `storelocations` is platform-authored,
 * so there is no consumer create path: `create()` + `POST /api/storelocations`
 * were removed, and `update` narrows to household PREFERENCE fields (identity
 * fields would 400 against the `.strict()` PATCH schema). "Add an online store"
 * folds into the selection/import flow.
 */

import { z } from 'zod';
import type { HttpClient } from './client';
import {
  type StoresResponse,
  type ToggleFavoriteResponse,
  storesResponseSchema,
  toggleFavoriteResponseSchema,
} from '../types/retailer';

const successSchema = z.object({ success: z.boolean() });

const API_BASE = '/api/storelocations';

// TD39 — preference-only update. `storelocations` identity (name / address /
// coords / place_id / online / hours) is platform-authored and can no longer be
// written by a household; the backend PATCH schema is `.strict()` and 400s any
// identity field. Only the per-household favourite flag rides through this path
// (per-store category affinity has its own `PUT /selected-stores/:id/categories`).
export interface UpdateStoreRequest {
  isFavorite?: boolean;
}

export class StoresApi {
  constructor(private readonly http: HttpClient) {}

  fetchAll(): Promise<StoresResponse> {
    return this.http.get(API_BASE, storesResponseSchema);
  }

  async update(storeId: string, input: UpdateStoreRequest): Promise<void> {
    await this.http.patch(
      `${API_BASE}/${encodeURIComponent(storeId)}`,
      input,
      successSchema
    );
  }

  delete(storeId: string): Promise<void> {
    return this.http.delete(`${API_BASE}/${encodeURIComponent(storeId)}`);
  }

  toggleFavorite(storeId: string): Promise<ToggleFavoriteResponse> {
    return this.http.post(
      `${API_BASE}/${encodeURIComponent(storeId)}/favorite`,
      {},
      toggleFavoriteResponseSchema
    );
  }
}
